import { useCallback } from 'react';
import { CommonActions } from '@react-navigation/native';
import { useAppDownloadModal } from '../component';
import { useWebviewBottomSheet } from '../component/WebviewBottomSheet';
import { bridge, isWeb } from '../utils';
import { convertDeeplinkToWebLink, resolveWebviewDeeplinkUrl } from '../utils';
import { resolveWebviewBottomSheetTarget } from '../utils/webviewBottomSheetDeeplink';
import { isAppDownloadDeeplink } from '../utils/appDownloadDeeplink';
import {
  HOME_TAB_ROUTE_NAME,
  TAB_HOST_ROUTE_NAME,
  useRootStackNavigation,
} from './useRootNavigation';
import { useWebRoutePush } from './useWebRoutePush';
import {
  isEmbeddedWebviewTarget,
  resolveScreenFromPath,
} from '../webRoutePaths';
import {
  resolveBottomTabFromDeeplink,
  resolveHomeTabFromDeeplink,
} from '../utils/deeplinkToHomeTab';
import { isNotNil } from '../utils';
import { useExperimentGroup } from '../context/UserContext';

export const useOpenDeeplink = () => {
  const { show: showAppDownloadModal } = useAppDownloadModal();
  const { show: showWebviewBottomSheet } = useWebviewBottomSheet();
  const navigation = useRootStackNavigation();

  const isMainRN = useExperimentGroup({
    sno: 'EXP1795',
    targetGroups: ['B'],
  });
  const { pushResolvedScreen, pushWebviewWrappedScreen } = useWebRoutePush();

  const moveWithReactNavigation = useCallback(
    async (link: string) => {
      // 탭에 대응되는 딥링크는 bridge 우회하고 RN navigation 으로 직접 처리.
      //
      // 개별 탭은 하단탭 호스트(native: 'Home' / web: 'tab') **안쪽** 라우트라, 루트에서
      // navigate('HOME') 하면 "not handled by any navigator" 가 된다. 중첩 형태로 보낸다.
      // CommonActions 를 쓰는 이유: RootStackParamList 인덱스 시그니처가 `params?: string`
      // 이라 타입드 API 로는 중첩 params 객체를 넘길 수 없다.

      // 하단탭 자체로 이동 (ably://mypage 등)
      const bottomTab = resolveBottomTabFromDeeplink(link);
      if (isNotNil(bottomTab)) {
        navigation.dispatch(
          CommonActions.navigate(TAB_HOST_ROUTE_NAME, { screen: bottomTab }),
        );
        return;
      }

      // 하단탭 HOME 안의 상단탭 지정 (ably://home/today 등)
      const homeTab = resolveHomeTabFromDeeplink(link);
      if (isNotNil(homeTab)) {
        navigation.dispatch(
          CommonActions.navigate(TAB_HOST_ROUTE_NAME, {
            screen: HOME_TAB_ROUTE_NAME,
            params: {
              selected: homeTab.screenName.toLowerCase(),
              ...homeTab.params,
            },
          }),
        );
        return;
      }
      // 바텀시트로 띄울 딥링크(`ably://webview-bottomsheet`, 그리고 시트로 봐야 하는
      // `ably://webview` 경로들)는 화면 이동 없이 시트로 흡수한다.
      //
      // 웹 전용: 네이티브는 bridge 가 `webview-bottomsheet` 를 직접 처리하고, 여기(RN)로는
      // 이 흐름을 타지 않는 편이 자연스럽다.
      if (isWeb) {
        const bottomSheetTarget = resolveWebviewBottomSheetTarget(link);
        if (isNotNil(bottomSheetTarget)) {
          showWebviewBottomSheet({
            url: bottomSheetTarget.url,
            heightRatio: bottomSheetTarget.heightRatio,
          });
          return;
        }
      }

      if (!isWeb) {
        if (link.startsWith('ably://webview')) {
          const webviewUrl = link.replace('ably://webview?url=', '');
          // 전용 화면이 있는 경로면 그쪽으로.
          if (pushWebviewWrappedScreen(webviewUrl)) return;
          // ably webview 인 경우
          navigation.push('WebViewScreen', {
            url: webviewUrl,
          });
          return;
        }
      }

      // web: 기본은 실제 웹으로 내보내고(새 탭), 지정된 경로만 앱 안 WebViewScreen 으로 띄운다.
      // url 값이 path 만 있으면 환경 origin(prod / 스테이징 / 로컬 :3000)을 붙인다.
      //
      // ⚠️ 아래 await(convertDeeplinkToWebLink) 보다 먼저, 동기적으로 판단해야 한다. await 를
      // 거치면 브라우저 user activation 이 끊겨 window.open 이 팝업 차단에 걸린다.
      // 웹 미지원(네이티브 전용) 경로면 null 이 와서 기존 흐름(앱 다운로드 유도)으로 흘러간다.
      if (isWeb && link.startsWith('ably://webview')) {
        const webviewUrl = resolveWebviewDeeplinkUrl(link);
        if (webviewUrl !== null) {
          // 전용 화면이 있는 경로면 그쪽으로(Cart/OrderDetail/EventDetail).
          // 주소가 목적지 경로로 유지된다. 네이티브 분기와 같은 규칙.
          if (pushWebviewWrappedScreen(webviewUrl)) return;

          // 스택 안에 남아야 하는 경로는 새 탭 대신 임베드. window.open 은 RN 네비게이션을
          // 벗어나 뒤로가기·스택 복귀가 끊긴다. 대상은 WEB_EMBEDDED_WEBVIEW_PATH_PREFIXES.
          if (isEmbeddedWebviewTarget(webviewUrl)) {
            navigation.push('WebViewScreen', { url: webviewUrl });
            return;
          }

          window.open(webviewUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      const webLink = await convertDeeplinkToWebLink(link);
      if (webLink == null) {
        showAppDownloadModal();
        return;
      }

      // 외부/절대 URL은 location.href로 이동
      if (webLink.startsWith('http')) {
        location.href = webLink;
        return;
      }

      // TODO 나중엔 요걸 webCallback내부에서 하지 않고, bridge.openDeeplink 대신 호출하도록 수정함
      // 내부 경로: webRoutePaths에서 screen name 매칭 → RN 네비게이션
      const resolved = resolveScreenFromPath(webLink);
      if (resolved != null) {
        pushResolvedScreen(resolved);
        return;
      }

      // 매칭 안 되면 fallback
      location.href = webLink;
    },
    [
      navigation,
      showAppDownloadModal,
      showWebviewBottomSheet,
      pushResolvedScreen,
      pushWebviewWrappedScreen,
    ],
  );

  const openDeeplink = useCallback(
    (link: string) => {
      // 앱 설치 유도(`ably://app-download`)는 이동이 아니라 모달이다. **웹 전용** —
      // 앱에서는 유도할 게 없으므로 아무 동작도 하지 않는다(bridge 로도 넘기지 않는다).
      if (isAppDownloadDeeplink(link)) {
        if (isWeb) showAppDownloadModal();
        return;
      }

      if (isMainRN) {
        moveWithReactNavigation(link);
        return;
      }
      bridge.openDeeplink(link, {
        webCallback: () => moveWithReactNavigation(link),
      });
    },
    [isMainRN, moveWithReactNavigation, showAppDownloadModal],
  );

  return { openDeeplink };
};
